import {
  deleteAlertRuleList,
  postAlertRuleList,
  putAlertRuleList,
} from "api/alertAPI";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  Alert,
  AlertRuleGroupRecipients,
  AlertRuleListResponse,
  AlertRuleRecipients,
  PostAlertRequest,
} from "api/interfaces/alertInterface.interface";
import {
  ALERT_DETECT_TIME,
  CustomError,
  LEVEL_TYPE,
  Validate,
  ValidateMsg,
} from "api/interfaces/commonInterface.interface";
import { useAuth } from "components/Auth";
import { useCallback, useEffect, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { AddRulePresenter } from "./AddRulePresenter";
import useApiError from "hook/useApiError";
import useStatusMutation from "hook/useStatusMutation";
import { StepType } from "components/blocks/stepper/Stepper";
import { checkEmpty, toTrim } from "utils/functions";

interface Props {
  onModal: () => void;
  isEdit?: boolean;
  isView?: boolean;
  editData?: PostAlertRequest;
  alertRule?: AlertRuleListResponse;
}

export enum UPDATE_TYPE {
  AUTO_APPLY_RECORDER,
  AUTO_APPLY_ACCOUNT,
  ACCOUNT,
  RECORDER,
  RECORDER_GROUP,
  USER,
  USER_GROUP,
  SYSTEM_ENABLE,
  SYSTEM_HARDWAREFAILURE,
  SYSTEM_NOTRESPONSE,
  SYSTEM_NOTRESPONSE_DETECTTIME,
  SYSTEM_ABNORMALSTART,
  SYSTEM_OVERHEATED,
  DISK_ENABLE,
  DISK_OVERHEATED,
  DISK_BADSECTOR,
  DISK_STORAGE_STATUS,
  DISK_REINDEXING,
  DISK_STORAGE_SPACE_FULL,
  DISK_RESERVE_SPACE_CLEAR,
  VIDEO_ENABLE,
  VIDEO_LOST_CAMERA,
  VIDEO_LOST_CAMERA_DETECTTIME,
  VIDEO_RECORDING,
  RULE_NAME,
  HOST,
  DESC,
  EMAIL,
  ENABLE,
}

const minMinuteSystemNotResponse = 15;

function AddRuleContainer(props: Props): JSX.Element {
  const auth = useAuth();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const initialData: PostAlertRequest = {
    isDefault: false,
    isEnable: true,
    level: selectedAccount.accountLevel,
    accountRule: {
      autoApplyAccount: false,
      accounts: {
        cp: [],
        eu: [],
      },
    },
    recorderRule: {
      autoApplyRecorder: false,
      systems: [],
      recorderGroups: [],
    },
    recipientsRule: {
      recipients: [],
      recipientUserGroups: [],
    },
    preferenceRule: {
      ruleName: "",
      host: auth.user?.name || "",
      email: auth.user?.email || "",
      description: "",
    },
    systemAlert: {
      isEnable: true,
      isCriticalNotResponse: true,
      notResponse: 30,
      notResponseType: ALERT_DETECT_TIME.MIN,
      isCriticalAbnormalStart: false,
      abnormalStart: 3,
      abnormalStartType: ALERT_DETECT_TIME.DAY,
      isCriticalOverheated: false,
      overHeated: 1,
      overHeatedType: ALERT_DETECT_TIME.DAY,
      isCriticalVideoRecording: true,
    },
    diskAlert: {
      isEnable: true,
      isCriticalHardwareFailure: true,
      isCriticalOverheated: false,
      overheated: 1,
      overheatedType: ALERT_DETECT_TIME.DAY,
      isCriticalBadSectorOver: false,
      badSectorOver: 3,
      isCriticalStorageStatus: false,
      storageStatus: 1,
      storageStatusType: ALERT_DETECT_TIME.HOUR,
      isCriticalReindexing: false,
      reindexing: 24,
      reindexingType: ALERT_DETECT_TIME.HOUR,
      isCriticalStorageSpaceNearFull: false,
      storageSpaceNearFull: 1,
      storageSpaceNearFullType: ALERT_DETECT_TIME.HOUR,
      isCriticalReservedSpaceNotClearing: false,
      reservedSpaceNotClearing: 1,
      reservedSpaceNotClearingType: ALERT_DETECT_TIME.HOUR,
    },
    videoAlert: {
      isEnable: true,
      isCriticalLostConCamera: false,
      lostConCamera: 12,
      lostConCameraType: ALERT_DETECT_TIME.HOUR,
    },
  };
  const { handleErrorNotify, lang, intlString, notifySuccess } = useApiError();

  const [page, setPage] = useState<number>(0);
  const [checkedAccount, setCheckedAccount] = useState<string[]>([]);
  const [originalCheckedAccount, setOriginalCheckedAccount] = useState<
    string[]
  >([]);
  const [alertInfo, setAlertInfo] = useState<PostAlertRequest>(
    props.isEdit && props.editData
      ? props.editData
      : {
          ...initialData,
          level: selectedAccount.accountLevel,
          preferenceRule: {
            ...initialData.preferenceRule,
            host: auth.user?.name,
            email: auth.user?.email,
          },
        }
  );
  const isEmptyRuleName = checkEmpty(alertInfo?.preferenceRule?.ruleName);

  useEffect(() => {
    if (props.editData) {
      setAlertInfo({
        ...initialData,
        ...props.editData,
      });

      const originalChecked = mergedCheckAccount(props.editData);
      setOriginalCheckedAccount(originalChecked);
      setCheckedAccount(originalChecked);
    }
  }, [props.editData, selectedAccount.accountLevel]);

  const mergedCheckAccount = (alert: Alert) => {
    let mergedAccount: string[] = [];
    if (alert.accountRule?.accounts?.cp !== undefined) {
      const tmpCp = alert.accountRule.accounts?.cp.map((value) =>
        value.concat("_", "CP")
      );
      mergedAccount.push(...tmpCp);
    }

    if (alert.accountRule?.accounts?.eu !== undefined) {
      const tmpEu = alert.accountRule?.accounts?.eu.map((value) =>
        value.concat("_", "EU")
      );
      mergedAccount.push(...tmpEu);
    }
    return mergedAccount;
  };

  const steps: StepType[] = [
    {
      label:
        selectedAccount.accountLevel !== LEVEL_TYPE.EU
          ? "Organizations"
          : "Recorders",
    },
    {
      label: "Rule",
    },
    {
      label: "Recipients",
    },
    {
      label: "Preferences",
    },
  ];

  const [alertRuleValidate, setAlertRuleValidate] = useState<Validate>({
    isRecorder: false,
    isAccount: false,
    isRuleName: false,
    isRule: false,
  });
  const [alertRuleValidateMsg, setAlertRuleValidateMsg] = useState<ValidateMsg>(
    {
      isRecorder: "",
      isAccount: "",
      isRuleName: "",
      isRule: "",
    }
  );

  const initAlertRuleValidate = useCallback((field: string) => {
    setAlertRuleValidate((info) => {
      return { ...info, [field as keyof typeof alertRuleValidate]: false };
    });
    setAlertRuleValidateMsg((info) => {
      return { ...info, [field as keyof typeof alertRuleValidateMsg]: "" };
    });
  }, []);

  const onChangePage = (name: string, index?: number) => {
    const isEmptyRecorder =
      selectedAccount.accountLevel === LEVEL_TYPE.EU &&
      !alertInfo.recorderRule.autoApplyRecorder &&
      (alertInfo.recorderRule.systems?? []).length === 0 &&
      (alertInfo.recorderRule.recorderGroups?? []).length === 0;
    const isEmptyAccount =
      selectedAccount.accountLevel === LEVEL_TYPE.CP &&
      !alertInfo.accountRule.autoApplyAccount &&
      checkedAccount.length === 0;
    const isEmptyRule =
      !alertInfo.videoAlert.isEnable &&
      !alertInfo.diskAlert.isEnable &&
      !alertInfo.systemAlert.isEnable;

    if (name !== "previous") {
      switch (page) {
        case 0:
          setAlertRuleValidate((info) => {
            return { ...info, isRecorder: isEmptyRecorder as boolean };
          });

          setAlertRuleValidateMsg((info) => {
            return {
              ...info,
              isRecorder: isEmptyRecorder
                ? intlString(lang.validateMsg.userGroup.recorder)
                : "",
            };
          });
          if (isEmptyRecorder) {
            return;
          }

          setAlertRuleValidate((info) => {
            return { ...info, isAccount: isEmptyAccount as boolean };
          });

          setAlertRuleValidateMsg((info) => {
            return {
              ...info,
              isAccount: isEmptyAccount
                ? intlString(lang.validateMsg.alert.rule.emptyAccount)
                : "",
            };
          });
          if (isEmptyAccount) {
            return;
          }
          break;
        case 1:
          setAlertRuleValidate((info) => {
            return { ...info, isRule: isEmptyRule as boolean };
          });

          setAlertRuleValidateMsg((info) => {
            return {
              ...info,
              isRule: isEmptyRule
                ? intlString(lang.validateMsg.alert.rule.emptyRule)
                : "",
            };
          });
          if (isEmptyRule) {
            return;
          }
          break;
        case 3:
          setAlertRuleValidate((info) => {
            return { ...info, isRuleName: isEmptyRuleName as boolean };
          });

          setAlertRuleValidateMsg((info) => {
            return {
              ...info,
              isRuleName: isEmptyRuleName
                ? intlString(lang.validateMsg.alert.rule.emptyRuleName)
                : "",
            };
          });
          break;
      }
    }
    if (name === "next") {
      setPage(page + 1);
    }
    if (name === "previous") {
      setPage(page - 1);
    }
    if (name === "save") {
      props.onModal();
    }
    if (name === "step" && index !== undefined) {
      setPage(index);
    }
  };

  const mutationCreateAlert = useStatusMutation(postAlertRuleList, {
    onSuccess: () => {
      notifySuccess(lang.label.alertRule.notify.add.success);
      props.onModal();
      // queryClient.invalidateQueries("alertList");
    },
    onError: (err: CustomError) => {
      console.log(err);
      handleErrorNotify(err, lang.label.alertRule.notify.add.fail);
    },
  });

  const mutationSaveAlert = useStatusMutation(putAlertRuleList, {
    onSuccess: () => {
      notifySuccess(lang.label.alertRule.notify.update.success);
      props.onModal();
      // queryClient.invalidateQueries("alertList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.alertRule.notify.update.fail);
    },
  });

  const onClickCreate = useCallback(() => {
    //setIsAddModal(true);
    initAlertRuleValidate("isRecorder");
    initAlertRuleValidate("isAccount");
    initAlertRuleValidate("isRuleName");

    if (selectedAccount.accountLevel === LEVEL_TYPE.CP) {
      let cpList: string[] = [];
      let euList: string[] = [];
      checkedAccount.forEach((value) => {
        if (value.endsWith(LEVEL_TYPE.CP)) {
          cpList.push(value.replace(`_CP`, ``));
        } else if (value.endsWith(LEVEL_TYPE.EU)) {
          euList.push(value.replace(`_EU`, ``));
        }
      });
      setAlertInfo((info) => {
        info.accountRule.accounts = {
          cp: cpList,
          eu: euList,
        };
        return info;
      });
    }

    if (isEmptyRuleName) {
      setAlertRuleValidate((info) => {
        return { ...info, isRuleName: true as boolean };
      });

      setAlertRuleValidateMsg((info) => {
        return {
          ...info,
          isRuleName: intlString(lang.validateMsg.alert.rule.emptyRuleName),
        };
      });
      setAlertInfo((info) => {
        return {
          ...info,
          preferenceRule: {
            ...info.preferenceRule,
            ruleName: toTrim(info.preferenceRule.ruleName),
            description: toTrim(info.preferenceRule.description),
          },
        };
      });
      setPage(3);
      return;
    }

    createAndUpdateRule();
  }, [alertInfo, checkedAccount]);

  const createAndUpdateRule = useCallback(() => {
    if (
      !mutationCreateAlert.idle ||
      !mutationSaveAlert.idle ||
      !mutationDeleteAlert.idle
    )
      return;
    const payload = {
      ...alertInfo,
      preferenceRule: {
        ...alertInfo.preferenceRule,
        ruleName: toTrim(alertInfo.preferenceRule.ruleName),
        description: toTrim(alertInfo.preferenceRule.description),
      },
    };
    if (props.isEdit) {
      mutationSaveAlert.mutate({
        accountId: selectedAccount.accountId,
        alertRuleId: props.alertRule?.alertRuleId as string,
        payload: payload,
      });
    } else {
      mutationCreateAlert.mutate({
        payload: payload,
        accountId: selectedAccount.accountId,
      });
    }
  }, [
    alertInfo,
    mutationCreateAlert,
    mutationSaveAlert,
    props.alertRule,
    props.isEdit,
    selectedAccount.accountId,
  ]);

  const mutationDeleteAlert = useStatusMutation(deleteAlertRuleList, {
    onSuccess: () => {
      notifySuccess(lang.label.alertRule.notify.delete.success);
      props.onModal();
      // queryClient.invalidateQueries("alertList");
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.alertRule.notify.delete.fail);
    },
  });

  const onClickDelete = useCallback(() => {
    if (
      !mutationCreateAlert.idle ||
      !mutationSaveAlert.idle ||
      !mutationDeleteAlert.idle
    )
      return;
    mutationDeleteAlert.mutate({
      accountId: selectedAccount.accountId,
      alertRuleId: props.alertRule?.alertRuleId as string,
    });
  }, [mutationDeleteAlert, props.alertRule, selectedAccount.accountId]);

  const onChangeAlertRuleInfo = useCallback(
    (
      type: UPDATE_TYPE,
      value: ALERT_DETECT_TIME | boolean | string | number | string[] | number[]
    ) => {
      if (type === UPDATE_TYPE.AUTO_APPLY_RECORDER) {
        setAlertInfo((info) => {
          return {
            ...info,
            recorderRule: {
              ...info.recorderRule,
              autoApplyRecorder: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.AUTO_APPLY_ACCOUNT) {
        setAlertInfo((info) => {
          return {
            ...info,
            accountRule: {
              ...info.accountRule,
              autoApplyAccount: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.ENABLE) {
        setAlertInfo((info) => {
          return { ...info, isEnable: value as boolean };
        });
      } else if (type === UPDATE_TYPE.ACCOUNT) {
        setCheckedAccount(value as string[]);
        // setAlertInfo((info) => {
        //   return {
        //     ...info,
        //     accountRule: {
        //       ...info.accountRule,
        //       accounts: value as string[],
        //     },
        //   };
        // });
      } else if (type === UPDATE_TYPE.RECORDER) {
        setAlertInfo((info) => {
          return {
            ...info,
            recorderRule: {
              ...info.recorderRule,
              systems: value as string[],
            },
          };
        });
      } else if (type === UPDATE_TYPE.RECORDER_GROUP) {
        setAlertInfo((info) => {
          return {
            ...info,
            recorderRule: {
              ...info.recorderRule,
              recorderGroups: value as number[],
            },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_ENABLE) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: { ...info.systemAlert, isEnable: value as boolean },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_NOTRESPONSE) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: { ...info.systemAlert, notResponse: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_NOTRESPONSE_DETECTTIME) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              notResponseType: value as ALERT_DETECT_TIME,
            },
          };
        });
        onCheckMinMaxValue(UPDATE_TYPE.SYSTEM_NOTRESPONSE, value);
      } else if (type === UPDATE_TYPE.SYSTEM_OVERHEATED) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: { ...info.systemAlert, overHeated: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_ABNORMALSTART) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              abnormalStart: value as number,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_ENABLE) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: { ...info.diskAlert, isEnable: value as boolean },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_OVERHEATED) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: { ...info.diskAlert, overheated: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_BADSECTOR) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: { ...info.diskAlert, badSectorOver: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_STORAGE_STATUS) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: { ...info.diskAlert, storageStatus: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_REINDEXING) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: { ...info.diskAlert, reindexing: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_STORAGE_SPACE_FULL) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              storageSpaceNearFull: value as number,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_RESERVE_SPACE_CLEAR) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              reservedSpaceNotClearing: value as number,
            },
          };
        });
      } else if (type === UPDATE_TYPE.VIDEO_ENABLE) {
        setAlertInfo((info) => {
          return {
            ...info,
            videoAlert: { ...info.videoAlert, isEnable: value as boolean },
          };
        });
      } else if (type === UPDATE_TYPE.VIDEO_LOST_CAMERA) {
        setAlertInfo((info) => {
          return {
            ...info,
            videoAlert: { ...info.videoAlert, lostConCamera: value as number },
          };
        });
      } else if (type === UPDATE_TYPE.VIDEO_LOST_CAMERA_DETECTTIME) {
        setAlertInfo((info) => {
          return {
            ...info,
            videoAlert: {
              ...info.videoAlert,
              lostConCameraType: value as ALERT_DETECT_TIME,
            },
          };
        });
      } else if (type === UPDATE_TYPE.USER) {
        setAlertInfo((info) => {
          return {
            ...info,
            recipientsRule: {
              ...info.recipientsRule,
              userId: value as string[],
            },
          };
        });
      } else if (type === UPDATE_TYPE.RULE_NAME) {
        setAlertInfo((info) => {
          return {
            ...info,
            preferenceRule: {
              ...info.preferenceRule,
              ruleName: value as string,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setAlertInfo((info) => {
          return {
            ...info,
            preferenceRule: {
              ...info.preferenceRule,
              description: value as string,
            },
          };
        });
      }
    },
    [alertInfo, checkedAccount]
  );

  const onChangeRecipients = (
    value: AlertRuleRecipients[] | AlertRuleGroupRecipients[],
    isGroup?: boolean
  ) => {
    if (isGroup) {
      setAlertInfo((info) => {
        return {
          ...info,
          recipientsRule: {
            ...info.recipientsRule,
            recipientUserGroups: value as AlertRuleGroupRecipients[],
          },
        };
      });
      return;
    }
    setAlertInfo((info) => {
      return {
        ...info,
        recipientsRule: {
          ...info.recipientsRule,
          recipients: value as AlertRuleRecipients[],
        },
      };
    });
  };

  const onCheckMinMaxValue = useCallback(
    (
      type: UPDATE_TYPE,
      value: ALERT_DETECT_TIME | boolean | string | number | string[] | number[]
    ) => {
      if (
        type === UPDATE_TYPE.SYSTEM_NOTRESPONSE &&
        value === ALERT_DETECT_TIME.MIN &&
        alertInfo.systemAlert.notResponse < minMinuteSystemNotResponse
      ) {
        onChangeAlertRuleInfo(type, minMinuteSystemNotResponse);
      }
    },
    [alertInfo.systemAlert.notResponse, alertInfo.systemAlert.notResponseType]
  );

  const onChangeAlertRuleCheckInfo = useCallback(
    (type: UPDATE_TYPE, value: boolean) => {
      if (type === UPDATE_TYPE.SYSTEM_NOTRESPONSE) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              isCriticalNotResponse: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_HARDWAREFAILURE) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              isCriticalHardwareFailure: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_OVERHEATED) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              isCriticalOverheated: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.SYSTEM_ABNORMALSTART) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              isCriticalAbnormalStart: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_OVERHEATED) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              isCriticalOverheated: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_BADSECTOR) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              isCriticalBadSectorOver: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_STORAGE_STATUS) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              isCriticalStorageStatus: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_REINDEXING) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              isCriticalReindexing: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_STORAGE_SPACE_FULL) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              isCriticalStorageSpaceNearFull: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.DISK_RESERVE_SPACE_CLEAR) {
        setAlertInfo((info) => {
          return {
            ...info,
            diskAlert: {
              ...info.diskAlert,
              isCriticalReservedSpaceNotClearing: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.VIDEO_LOST_CAMERA) {
        setAlertInfo((info) => {
          return {
            ...info,
            videoAlert: {
              ...info.videoAlert,
              isCriticalLostConCamera: value as boolean,
            },
          };
        });
      } else if (type === UPDATE_TYPE.VIDEO_RECORDING) {
        setAlertInfo((info) => {
          return {
            ...info,
            systemAlert: {
              ...info.systemAlert,
              isCriticalVideoRecording: value as boolean,
            },
          };
        });
      }
    },
    []
  );

  return (
    <AddRulePresenter
      onModal={props.onModal}
      page={page}
      onChangePage={onChangePage}
      isEdit={props.isEdit}
      isView={props.isView}
      steps={steps}
      data={alertInfo}
      selectedRecorder={alertInfo.recorderRule?.systems}
      selectedAccountList={originalCheckedAccount}
      selectedRecorderGroup={alertInfo.recorderRule?.recorderGroups}
      selectedUser={alertInfo.recipientsRule.recipients}
      selectedGroup={alertInfo.recipientsRule.recipientUserGroups}
      hostName={auth.user?.name + "(" + auth.user?.email + ")"}
      onChangeAlertRuleInfo={onChangeAlertRuleInfo}
      onClickCreate={onClickCreate}
      onClickSave={onClickCreate}
      onClickDelete={onClickDelete}
      onChangeAlertRuleCheckInfo={onChangeAlertRuleCheckInfo}
      onChangeRecipients={onChangeRecipients}
      alertRuleValidate={alertRuleValidate}
      alertRuleValidateMsg={alertRuleValidateMsg}
      userId={props.isEdit ? props.alertRule?.userId : auth.user.userId}
      disabledSave={!mutationCreateAlert.idle || !mutationSaveAlert.idle}
      disabledDelete={!mutationDeleteAlert.idle}
      onCheckMinMaxValue={onCheckMinMaxValue}
    />
  );
}

export default AddRuleContainer;
